import {
  Bell,
  BookOpen,
  CalendarDays,
  ChevronRight,
  CircleDollarSign,
  Clock3,
  LogOut,
  Mail,
  MapPin,
  Phone,
  School,
  ShieldCheck,
  Sparkles,
  UserRound,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { LoadingState } from '../../components/common/LoadingState';
import { getCourse, getStudentCourseSignups, getTenant } from '../../firebase/firestore';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import { StudentSidebar } from './StudentSidebar';

function readableStatus(value) {
  return String(value || 'enrolled').replace(/_/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());
}

function formatJoinedDate(value) {
  if (!value) return 'Not available';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not available';
  return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }).format(date);
}

export function StudentProfilePage() {
  const { user, profile, loading: authLoading, logout } = useAuth();
  const { notify } = useToast();
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);

  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!user?.uid) {
        if (mounted) setLoading(false);
        return;
      }
      try {
        const signups = await getStudentCourseSignups(user.uid);
        const enriched = await Promise.all(signups.map(async (signup) => {
          const tenantId = signup.tenantId || signup.ref?.parent?.parent?.id || '';
          const [tenant, course] = await Promise.all([
            tenantId ? getTenant(tenantId) : Promise.resolve(null),
            tenantId && signup.courseId ? getCourse(tenantId, signup.courseId) : Promise.resolve(null),
          ]);
          return { ...signup, tenantId, tenant, course };
        }));
        if (mounted) {
          setRecords(enriched);
          setLoading(false);
        }
      } catch (error) {
        console.error('Unable to load student profile', error);
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [user?.uid]);

  async function onLogout() {
    await logout();
    notify('You have been signed out.');
  }

  if (authLoading) return <LoadingState label="Loading student portal..." />;
  if (!user) return <Navigate to="/student/login" replace />;

  const firstRecord = records[0];
  const accountEmail = user.email || profile?.email || firstRecord?.email || firstRecord?.authEmail || '';
  const displayName = profile?.displayName || user.displayName || String(accountEmail || 'Student').split('@')[0];
  const firstName = displayName.split(' ')[0];
  const initials = displayName.split(/\s+/).map((part) => part[0]).join('').slice(0, 2).toUpperCase();
  const phone = profile?.phone || firstRecord?.phone || 'Not provided';
  const country = profile?.country || profile?.phoneCountry || firstRecord?.countryOfResidence || firstRecord?.country || 'Not provided';
  const timezone = profile?.timezone || firstRecord?.timezone || 'Not provided';
  const currency = profile?.currency || firstRecord?.currency || 'Not provided';
  const studentId = firstRecord?.studentId || firstRecord?.admissionNumber || 'Assigned by your academy';
  const academyName = firstRecord?.tenant?.name || 'Student workspace';
  const activeEnrollments = records.filter((record) => ['active', 'admitted', 'approved', 'enrolled'].includes(String(record.status || '').toLowerCase())).length;
  const completionFields = [displayName, accountEmail, phone !== 'Not provided', country !== 'Not provided', timezone !== 'Not provided'];
  const profileCompletion = Math.round((completionFields.filter(Boolean).length / completionFields.length) * 100);
  const currentDate = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }).format(new Date());

  return (
    <main className="student-portal student-account-page">
      <StudentSidebar academyName={academyName} onLogout={onLogout} />

      <div className="student-account-shell">
        <section className="student-account-workspace">
          <header className="student-account-page-header">
            <div>
              <span>Welcome back, {firstName}</span>
              <h1>My Profile</h1>
              <p>{currentDate}</p>
            </div>
            <button type="button" className="student-dashboard-notification" aria-label="Notifications">
              <Bell size={19} strokeWidth={1.7} />
              <i />
            </button>
          </header>

          <section className="student-account-identity-card">
            <div className="student-account-avatar">
              {user.photoURL ? <img src={user.photoURL} alt={displayName} /> : <strong>{initials}</strong>}
              <i className="shape-one" /><i className="shape-two" />
            </div>
            <div className="student-account-identity-copy">
              <span>Student account</span>
              <h2>{displayName}</h2>
              <p>{accountEmail || 'Email not provided'}</p>
            </div>
            <span className="student-account-active"><i />Active account</span>
          </section>

          <div className="student-account-section-heading">
            <div><h2>Personal information</h2><p>Your account and student contact details.</p></div>
            <span><ShieldCheck size={14} />Secure profile</span>
          </div>

          <section className="student-account-information">
            <article><span><Mail size={17} /></span><div><small>Email address</small><strong>{accountEmail || 'Not provided'}</strong></div></article>
            <article><span><Phone size={17} /></span><div><small>Phone number</small><strong>{phone}</strong></div></article>
            <article><span><MapPin size={17} /></span><div><small>Country</small><strong>{country}</strong></div></article>
            <article><span><Clock3 size={17} /></span><div><small>Timezone</small><strong>{timezone}</strong></div></article>
            <article><span><CircleDollarSign size={17} /></span><div><small>Currency</small><strong>{currency}</strong></div></article>
            <article><span><UserRound size={17} /></span><div><small>Student ID</small><strong>{studentId}</strong></div></article>
            <article><span><CalendarDays size={17} /></span><div><small>Account created</small><strong>{formatJoinedDate(user.metadata?.creationTime)}</strong></div></article>
          </section>

          <div className="student-account-section-heading student-account-learning-heading">
            <div><h2>Learning connections</h2><p>Academies and courses connected to this student account.</p></div>
            <span>{records.length} enrollment{records.length === 1 ? '' : 's'}</span>
          </div>

          {loading ? (
            <div className="student-courses-inline-loading"><span /><p>Loading your learning connections...</p></div>
          ) : records.length ? (
            <section className="student-account-enrollments">
              {records.map((record, index) => (
                <article key={`${record.tenantId}-${record.id}`}>
                  <span className={`student-account-course-icon student-account-course-icon--${(index % 3) + 1}`}><BookOpen size={17} /></span>
                  <div><small>{record.tenant?.name || 'Academy'}</small><strong>{record.course?.name || record.courseName || 'Course'}</strong><p>{record.intakeLabel || 'Open intake'} · {record.intakeSessionLabel || 'Open session'}</p></div>
                  <span className="student-account-enrollment-status">{readableStatus(record.status)}</span>
                  {record.course?.id ? <Link to={`/student/courses/${record.tenantId}/${record.course.id}`} aria-label={`Open ${record.course.name}`}><ChevronRight size={16} /></Link> : null}
                </article>
              ))}
            </section>
          ) : (
            <div className="student-courses-empty"><School size={25} /><h2>No learning connections yet</h2><p>Your academy and courses will appear here after your enrollment is confirmed.</p></div>
          )}
        </section>

        <aside className="student-account-overview">
          <div className="student-account-overview-heading"><h2>Account Overview</h2><UserRound size={17} /></div>
          <div className="student-account-profile-art">
            {user.photoURL ? <img src={user.photoURL} alt={displayName} /> : <strong>{initials}</strong>}
            <i className="shape-one" /><i className="shape-two" /><i className="shape-three" />
          </div>
          <h3>{displayName}</h3>
          <p>{accountEmail || 'Email not provided'}</p>

          <div className="student-account-completion">
            <div><span>Profile completion</span><strong>{profileCompletion}%</strong></div>
            <i><span style={{ width: `${profileCompletion}%` }} /></i>
          </div>

          <div className="student-account-overview-stats">
            <div><strong>{records.length}</strong><small>Enrolled</small></div>
            <div><strong>{activeEnrollments}</strong><small>Active</small></div>
            <div><strong>{user.emailVerified ? 'Yes' : 'No'}</strong><small>Verified</small></div>
          </div>

          <div className="student-account-note">
            <span><Sparkles size={16} /></span>
            <small>One student identity</small>
            <h3>Your learning stays connected.</h3>
            <p>Use the same student account whenever you access your courses, classes and finance records.</p>
          </div>

          <button type="button" className="student-account-logout" onClick={onLogout}><LogOut size={15} />Log out of this account</button>
          <Link className="student-account-dashboard-link" to="/student/portal">Back to dashboard <ChevronRight size={16} /></Link>
        </aside>
      </div>
    </main>
  );
}
